import { DialogComponent } from '@theme/dialog';
import { debounce } from '@theme/utilities';

/** How often the mobile title word rotates while the menu is open. */
const WORD_ROTATION_INTERVAL = 4000;

/**
 * The header buttons that open this menu — the compact header's and the two
 * hero variants' (sections/header.liquid). The close button is pinned to
 * whichever one is on screen.
 */
const HAMBURGER_SELECTOR = '.header__hamburger';

/** Matches the theme's mobile breakpoint (sections/mega-menu.liquid). */
const MOBILE_MEDIA_QUERY = '(max-width: 749px)';

/** CSS hooks for the sliding word window (sections/mega-menu.liquid). */
const WORD_ITEM_CLASS = 'mega-menu-overlay__title-word-item';
const NO_TRANSITION_CLASS = 'mega-menu-overlay__title-word-list--no-transition';

/**
 * A fullscreen mega menu dialog opened from the header hamburger.
 *
 * The title ends with a word picked at random from the `data-title-words`
 * attribute (one word per line) every time the menu opens. Hovering a menu
 * link swaps in that link's word — its `data-keyword` if set (e.g. a link
 * labelled "Four vineyards" can show "wine"), otherwise its visible text.
 * Leaving the link restores the random word.
 *
 * On mobile there is no hover, so while the menu is open the word instead
 * rotates to a new random pick every few seconds, drawn from the same pool
 * hover feeds on — every link's keyword — plus the configured title words
 * (the setting alone may hold a single word). The timer runs whenever the
 * dialog is open but only swaps the word while the mobile media query
 * matches, so resizing across the breakpoint self-corrects and desktop's
 * hover behavior is never fought over.
 *
 * A link to an anchor on the current page (e.g. /pages/land#damsteep-vineyard
 * clicked while on /pages/land) closes the menu before the fragment is
 * followed — see #handleLinkClick.
 *
 * @typedef {object} MegaMenuRefs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [topBar] - The top slab (title + close button).
 * @property {HTMLElement} [titleWord] - The one-line window at the end of the title.
 * @property {HTMLElement} [titleWordList] - The sliding word list inside the window.
 */
class MegaMenuComponent extends DialogComponent {
  /** The random word shown while no link is hovered. */
  #baseWord = '';

  /** @type {ReturnType<typeof setInterval> | undefined} */
  #wordTimer;

  connectedCallback() {
    super.connectedCallback();
    // Fires for every close path (close button, Esc, outside click).
    this.refs.dialog.addEventListener('close', this.#stopWordRotation);
    this.refs.dialog.addEventListener('close', this.#stopTrackingHamburger);
    this.refs.dialog.addEventListener('close', this.#stopInterceptingLinks);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stopWordRotation();
    this.#stopTrackingHamburger();
    this.#stopInterceptingLinks();
  }

  showDialog() {
    this.#randomizeTitleWord();
    super.showDialog();
    this.#startWordRotation();

    // super.showDialog() defers the scroll lock and showModal() to the next
    // frame; measure in a later callback of that same frame so the readings
    // reflect the locked page (below 990px the lock removes the document
    // scrollbar, widening the header) and still land before the first paint —
    // the slide and page-push animations start with correct values.
    requestAnimationFrame(this.#syncSliceMetrics);
    window.addEventListener('resize', this.#handleResizeWhileOpen);
    // On window, bubble phase: runs after the document-level trigger listeners
    // of the hash-opened overlays (be-newsletter-overlay.js and friends), so a
    // click one of those has already claimed is left to it.
    window.addEventListener('click', this.#handleLinkClick);
  }

  #startWordRotation() {
    this.#stopWordRotation();

    const words = this.#rotationWords;

    if (words.length < 2) return;

    this.#wordTimer = setInterval(() => {
      if (window.matchMedia(MOBILE_MEDIA_QUERY).matches) {
        this.#randomizeTitleWord(words, { animate: true });
      }
    }, WORD_ROTATION_INTERVAL);
  }

  #stopWordRotation = () => {
    clearInterval(this.#wordTimer);
    this.#wordTimer = undefined;
  };

  #stopTrackingHamburger = () => {
    window.removeEventListener('resize', this.#handleResizeWhileOpen);
  };

  /** A resize can swap which hamburger is on screen, and moves them all. */
  #handleResizeWhileOpen = debounce(() => this.#syncSliceMetrics(), 50);

  #stopInterceptingLinks = () => {
    window.removeEventListener('click', this.#handleLinkClick);
  };

  /**
   * Follow a menu link to an anchor on the current page by closing the menu
   * first, then jumping to the anchor. Left to the browser, the fragment
   * navigation scrolls the page underneath the still-open menu, and
   * closeDialog() then puts the scroll position back where it was before the
   * menu opened — the menu stays up and the anchor is never reached.
   *
   * Links to other pages, and clicks another component has already handled
   * (the hash-opened overlays), are left alone.
   *
   * @param {MouseEvent} event
   */
  #handleLinkClick = (event) => {
    if (event.defaultPrevented) return;
    // Modifier and non-primary clicks open a new tab or window as usual.
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;

    if (!(link instanceof HTMLAnchorElement) || !this.refs.dialog.contains(link)) return;
    if ((link.target && link.target !== '_self') || link.hasAttribute('download')) return;

    const url = new URL(link.href);

    if (!url.hash || !isCurrentPage(url)) return;

    event.preventDefault();
    this.#followAnchor(url.hash);
  };

  /**
   * @param {string} hash - The fragment to navigate to, leading `#` included.
   */
  async #followAnchor(hash) {
    await this.closeDialog();

    // The page is back where it was before the menu opened; now navigate as
    // the click would have — history entry, `:target`, `hashchange` and the
    // scroll container's smooth scroll included.
    if (window.location.hash !== hash) {
      window.location.hash = hash;
      return;
    }

    // The URL already carries this fragment (a deep link, or the same anchor
    // clicked twice): re-assigning it is a no-op, so scroll to it directly.
    let id = '';
    try {
      id = decodeURIComponent(hash.slice(1));
    } catch {
      return;
    }

    document.getElementById(id)?.scrollIntoView();
  }

  /** Re-measure everything the overlay pins to the page's geometry. */
  #syncSliceMetrics = () => {
    this.#pinCloseButton();
    this.#setPushHeight();
  };

  /**
   * Publish the top slab's height as --mega-menu-top-height on <body> for the
   * page-push animation (see the .page-wrapper rules in base.css). Fractional
   * rect height, not offsetHeight: the slab's own slide is translateY(-100%)
   * of the same box, so an exact match keeps the page's top edge glued to the
   * slab's bottom edge with no seam. Left set on close — the next open
   * re-measures before first paint.
   */
  #setPushHeight = () => {
    const { topBar } = this.#megaMenuRefs;

    if (!topBar) return;

    document.body.style.setProperty('--mega-menu-top-height', `${topBar.getBoundingClientRect().height}px`);
  };

  /**
   * Place the close button over the hamburger that opened the menu, so the two
   * icons occupy the same box no matter which scrollbars the browser draws —
   * see the .mega-menu-overlay__close comment in sections/mega-menu.liquid.
   *
   * The custom properties are left set on close: the next open paints at the
   * last known position, which is already correct unless the viewport changed.
   */
  #pinCloseButton = () => {
    const rect = this.#hamburgerRect;

    if (!rect) return;

    // clientWidth, not innerWidth: `fixed` resolves against the viewport, which
    // excludes a classic document scrollbar.
    const viewportWidth = document.documentElement.clientWidth;
    const inlineEnd = getComputedStyle(this).direction === 'rtl' ? rect.left : viewportWidth - rect.right;

    // While the menu is open, the page-push animation holds the page — the
    // hamburger with it — translated down by the top slab's height. Measure
    // against the resting position (offset is 0 when the page sits at rest)
    // so the pin lands where the hamburger returns to once the menu closes.
    const blockStart = rect.top - this.#pagePushOffset;

    this.style.setProperty('--mega-menu-close-block-start', `${blockStart}px`);
    this.style.setProperty('--mega-menu-close-inline-end', `${inlineEnd}px`);
  };

  /** The page-push translate currently applied to the page wrapper, in px. */
  get #pagePushOffset() {
    const wrapper = document.querySelector('.page-wrapper');

    if (!wrapper) return 0;

    const { translate } = getComputedStyle(wrapper);

    if (!translate || translate === 'none') return 0;

    // Serialized as "<x>" or "<x> <y>"; the push is vertical, so read y.
    const y = translate.split(' ')[1];

    return y ? parseFloat(y) || 0 : 0;
  }

  /**
   * The on-screen hamburger's box, or null if none is rendered. Hero pages keep
   * two in the DOM — the compact header is parked above the viewport until the
   * hero scrolls past — so pick by geometry rather than by `display`, taking the
   * topmost of whatever is actually within the viewport.
   *
   * @returns {DOMRect | null}
   */
  get #hamburgerRect() {
    /** @type {DOMRect | null} */
    let closest = null;

    for (const hamburger of document.querySelectorAll(HAMBURGER_SELECTOR)) {
      const rect = hamburger.getBoundingClientRect();

      if (!rect.width || !rect.height) continue;
      if (rect.bottom <= 0 || rect.top >= window.innerHeight) continue;
      if (!closest || rect.top < closest.top) closest = rect;
    }

    return closest;
  }

  /**
   * Swap the title word for the hovered link's keyword (or its visible text).
   *
   * @param {PointerEvent} event
   */
  showItemWord(event) {
    const link = event.target;

    if (!(link instanceof HTMLElement)) return;

    const word = link.dataset.keyword?.trim() || link.textContent?.trim();

    if (word) this.#setTitleWord(word);
  }

  /** Restore the random word when the pointer leaves a link. */
  restoreTitleWord() {
    if (this.#baseWord) this.#setTitleWord(this.#baseWord);
  }

  /** The settled title word — the last item in the sliding list. */
  get #currentTitleWord() {
    return this.#megaMenuRefs.titleWordList?.lastElementChild?.textContent?.trim() ?? '';
  }

  /** @param {string} word */
  #wordItem(word) {
    const item = document.createElement('span');
    item.className = WORD_ITEM_CLASS;
    item.textContent = word;
    return item;
  }

  /**
   * Replace the title word instantly (the hover path), discarding any
   * in-flight slide.
   *
   * @param {string} word
   */
  #setTitleWord(word) {
    const list = this.#megaMenuRefs.titleWordList;

    if (!list) return;

    list.classList.add(NO_TRANSITION_CLASS);
    list.style.setProperty('--title-word-offset', '0');
    list.replaceChildren(this.#wordItem(word));
    // Force reflow so re-enabling the transition doesn't animate the reset.
    void list.offsetHeight;
    list.classList.remove(NO_TRANSITION_CLASS);
  }

  /**
   * Scroll the new word up through the one-line window, mirroring
   * assets/booking-scroller.js: settle the previous slide (drop the word
   * that scrolled out and reset the offset with the transition suppressed —
   * no visible change), then queue the word below and translate up a line.
   *
   * @param {string} word
   */
  #slideTitleWord(word) {
    const list = this.#megaMenuRefs.titleWordList;

    if (!list) return;

    if (list.childElementCount > 1 && list.firstElementChild) {
      list.classList.add(NO_TRANSITION_CLASS);
      list.firstElementChild.remove();
      list.style.setProperty('--title-word-offset', '0');
      void list.offsetHeight;
      list.classList.remove(NO_TRANSITION_CLASS);
    }

    list.appendChild(this.#wordItem(word));
    list.style.setProperty('--title-word-offset', '1');
  }

  get #megaMenuRefs() {
    return /** @type {MegaMenuRefs} */ (/** @type {unknown} */ (this.refs));
  }

  /** The candidate title words from `data-title-words` (one per line). */
  get #titleWords() {
    return (this.dataset.titleWords ?? '')
      .split('\n')
      .map((word) => word.trim())
      .filter(Boolean);
  }

  /**
   * The mobile rotation pool: the configured title words plus every link's
   * hover keyword, deduplicated case-insensitively (first spelling wins).
   */
  get #rotationWords() {
    const seen = new Map(this.#titleWords.map((word) => [word.toLowerCase(), word]));

    for (const link of this.querySelectorAll('.mega-menu-overlay__link')) {
      if (!(link instanceof HTMLElement)) continue;

      const word = link.dataset.keyword?.trim() || link.textContent?.trim();

      if (word && !seen.has(word.toLowerCase())) seen.set(word.toLowerCase(), word);
    }

    return [...seen.values()];
  }

  /**
   * @param {string[]} [words] - The pool to pick from; defaults to the
   *   configured title words.
   * @param {object} [options]
   * @param {boolean} [options.animate] - Scroll the new word up through the
   *   window instead of swapping it in place.
   */
  #randomizeTitleWord(words = this.#titleWords, { animate = false } = {}) {
    if (!this.#megaMenuRefs.titleWordList) return;

    if (words.length < 2) {
      this.#baseWord = this.#currentTitleWord;
      return;
    }

    // Exclude the current word so the title visibly changes on each swap
    const currentWord = this.#currentTitleWord.toLowerCase();
    const candidates = words.filter((word) => word.toLowerCase() !== currentWord);
    const nextWord = candidates[Math.floor(Math.random() * candidates.length)];

    if (!nextWord) return;

    if (animate) {
      this.#slideTitleWord(nextWord);
    } else {
      this.#setTitleWord(nextWord);
    }

    this.#baseWord = nextWord;
  }
}

/**
 * Whether a URL points at the document currently loaded, ignoring its fragment.
 *
 * @param {URL} url
 * @returns {boolean}
 */
function isCurrentPage(url) {
  const { origin, pathname, search } = window.location;

  return url.origin === origin && url.pathname === pathname && url.search === search;
}

if (!customElements.get('mega-menu-component')) customElements.define('mega-menu-component', MegaMenuComponent);
